import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { useLangPrefix } from '../hooks/useLocalePath'
import ProfileModal from './ProfileModal'
import LanguageSwitcher from './LanguageSwitcher'
import '../styles/Navbar.css'

export default function Navbar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const prefix = useLangPrefix()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showProfile, setShowProfile] = useState(false)

  const NAV_MAIN = [
    { to: `${prefix}/dashboard`,   label: t('nav.dashboard'),   icon: '📊' },
    { to: `${prefix}/projects`,    label: t('nav.projects'),    icon: '📁' },
    { to: `${prefix}/assignments`, label: t('nav.assignments'), icon: '✅' },
    { to: `${prefix}/qa`,          label: t('nav.qa'),          icon: '📋' },
  ]

  const NAV_GESTION = [
    { to: `${prefix}/billing`,  label: t('nav.billing'),  icon: '💳' },
    { to: `${prefix}/api-keys`, label: t('nav.apiKeys'), icon: '🔑' },
    { to: `${prefix}/contact`,  label: t('nav.contact'),  icon: '✉️' },
  ]

  const NAV_DOCS = [
    { to: `${prefix}/docs`,     label: t('nav.documentation'), icon: '📖' },
    { to: `${prefix}/api-docs`, label: t('nav.apiReference'),  icon: '🔌' },
  ]

  const NAV_ADMIN = [
    { to: `${prefix}/admin`, label: t('nav.administration'), icon: '⚙️' },
  ]

  const handleLogout = async () => {
    await logout()
    navigate(`${prefix}/login`)
  }

  const getLinkClass = ({ isActive }) =>
    isActive ? 'nav-link active' : 'nav-link'

  const closeMenu = () => setMenuOpen(false)

  return (
    <>
      {/* Mobile overlay */}
      {menuOpen && (
        <div className="nav-overlay" onClick={closeMenu} aria-hidden="true" />
      )}

      <button
        className="app-menu-toggle"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={t('nav.openMenu')}
        aria-expanded={menuOpen}
      >
        <span className={`hamburger ${menuOpen ? 'open' : ''}`} />
      </button>

      <nav className={`navbar ${menuOpen ? 'navbar--open' : ''}`}>
        {/* Logo */}
        <NavLink to={`${prefix}/dashboard`} className="navbar-logo" onClick={closeMenu}>
          <span className="navbar-logo-text">Review</span>
          <span className="navbar-logo-accent">Sight</span>
        </NavLink>

        {/* Main section */}
        <div className="nav-section">
          <span className="nav-section-label">{t('nav.sections.main')}</span>
          {NAV_MAIN.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} className={getLinkClass} onClick={closeMenu}>
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
        </div>

        {/* Gestion section */}
        <div className="nav-section">
          <span className="nav-section-label">{t('nav.sections.management')}</span>
          {NAV_GESTION.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} className={getLinkClass} onClick={closeMenu}>
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
        </div>

        {/* Documentation section */}
        <div className="nav-section">
          <span className="nav-section-label">{t('nav.sections.documentation')}</span>
          {NAV_DOCS.map(({ to, label, icon }) => (
            <NavLink key={to} to={to} className={getLinkClass} onClick={closeMenu}>
              <span className="nav-icon">{icon}</span>
              <span className="nav-label">{label}</span>
            </NavLink>
          ))}
        </div>

        {/* Admin section – only for admins */}
        {user?.role === 'admin' && (
          <div className="nav-section">
            <span className="nav-section-label">{t('nav.sections.admin')}</span>
            {NAV_ADMIN.map(({ to, label, icon }) => (
              <NavLink key={to} to={to} className={getLinkClass} onClick={closeMenu}>
                <span className="nav-icon">{icon}</span>
                <span className="nav-label">{label}</span>
              </NavLink>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="nav-spacer" />

        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Bottom: user info + logout */}
        <div className="nav-bottom">
          <div
            className="nav-user nav-user-clickable"
            onClick={() => setShowProfile(true)}
            role="button"
            tabIndex={0}
            title={t('nav.editProfile')}
          >
            <div className="nav-user-avatar">
              {user?.email?.[0]?.toUpperCase() ?? '?'}
            </div>
            <span className="nav-user-email">{user?.email}</span>
            <span className="nav-user-edit">✏️</span>
          </div>
          <button className="nav-logout" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-label">{t('nav.logout')}</span>
          </button>
        </div>
      </nav>

      {/* Profile modal */}
      {showProfile && (
        <ProfileModal onClose={() => setShowProfile(false)} />
      )}
    </>
  )
}
